import { createApi } from '@reduxjs/toolkit/query/react';
import { Config } from '@/config/environment';
import { sfAuth } from '@/auth/SFAuth';
import { withRetry } from '@/utils/retryUtils';
import { emitSessionExpired } from '@/auth/sessionEvents';
import {
  buildRequestError,
  parseOperationName,
  truncateBody,
  type GraphQLErrorLike,
  type RequestError,
} from './requestError';
import type { TypedDocumentString } from './graphql';

const ENDPOINT_LABEL = 'wsapi';

type GraphQLDocument = string | TypedDocumentString<unknown, unknown>;

interface ParsedGraphQL {
  data?: unknown;
  errors?: GraphQLErrorLike[];
}

/** Read a response body once, returning parsed JSON if possible and a raw snippet either way */
const readResponseBody = async (response: Response): Promise<{ json?: ParsedGraphQL; snippet?: string }> => {
  const text = await response.text();
  if (!text) return {};
  try {
    return { json: JSON.parse(text) as ParsedGraphQL, snippet: truncateBody(text) };
  } catch {
    return { snippet: truncateBody(text) };
  }
};

/** Make a GraphQL fetch request with current auth headers */
const makeGraphQLRequest = async (endpoint: string, document: GraphQLDocument, variables?: unknown): Promise<Response> => {
  // Proactive token refresh before each request — log failures so they're visible
  await sfAuth.getAccessToken().catch((err) => {
    console.warn('Proactive token refresh failed:', err instanceof Error ? err.message : err);
  });
  const headers = sfAuth.getAuthHeaders();
  return fetch(endpoint, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ query: String(document), variables }),
  });
};

/** True when a GraphQL error set contains "not authorized" */
const hasAuthErrorMessage = (errors?: GraphQLErrorLike[]): boolean =>
  !!errors?.some((e) => e.message?.toLowerCase().includes('not authorized'));

/** Attempt a forced FA refresh. Returns true on success, false on refresh failure. */
const tryForceRefresh = async (): Promise<boolean> => {
  try {
    await sfAuth.getAccessToken(true);
    return true;
  } catch {
    return false;
  }
};

const sessionExpiredError = (url: string, operation: string | undefined): RequestError => {
  emitSessionExpired('Your session has expired. Please sign in again.');
  sfAuth.clearSession();
  return buildRequestError({
    kind: 'session-expired',
    endpoint: ENDPOINT_LABEL,
    url,
    operation,
    status: 401,
  });
};

const wsapiAuthRejected = (
  url: string,
  operation: string | undefined,
  bodySnippet?: string,
): RequestError =>
  buildRequestError({
    kind: 'wsapi-auth-rejected',
    endpoint: ENDPOINT_LABEL,
    url,
    operation,
    status: 401,
    bodySnippet,
  });

export const graphqlBaseQuery = async ({ document, variables }: { document: GraphQLDocument; variables?: unknown }) => {
  const url = Config.graphqlEndpoint;
  const operation = parseOperationName(String(document));

  let response: Response;
  try {
    response = await withRetry(
      async () => {
        const resp = await makeGraphQLRequest(url, document, variables);
        if (resp.status >= 500) {
          throw Object.assign(new Error(`Server error: ${resp.status}`), { response: { status: resp.status } });
        }
        return resp;
      },
      {
        maxRetries: 3,
        retryCondition: () => true, // Only 5xx and network errors reach here
        onRetry: (error, retryCount) => {
          console.warn(`API request retry attempt ${retryCount}:`, error.message);
        },
      },
    );
  } catch (error) {
    const httpStatus = (error as { response?: { status?: number } })?.response?.status;
    if (httpStatus && httpStatus >= 500) {
      return {
        error: buildRequestError({
          kind: 'http',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: httpStatus,
          detail: 'retries exhausted',
        }),
      };
    }
    return {
      error: buildRequestError({
        kind: 'network',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        detail: error instanceof Error ? error.message : 'Unknown network error',
      }),
    };
  }

  // 401 → force FA refresh, retry once
  if (response.status === 401) {
    const refreshed = await tryForceRefresh();
    if (!refreshed) {
      return { error: sessionExpiredError(url, operation) };
    }
    const retryResponse = await makeGraphQLRequest(url, document, variables);
    if (retryResponse.status === 401) {
      const { snippet } = await readResponseBody(retryResponse);
      return { error: wsapiAuthRejected(url, operation, snippet) };
    }
    response = retryResponse;
  }

  const { json, snippet } = await readResponseBody(response);

  if (!json) {
    return {
      error: buildRequestError({
        kind: 'http',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        status: response.status,
        bodySnippet: snippet,
        detail: 'non-JSON response',
      }),
    };
  }

  // GraphQL-level "not authorized" safety net: force FA refresh, retry once
  if (json.errors?.length && hasAuthErrorMessage(json.errors)) {
    const refreshed = await tryForceRefresh();
    if (!refreshed) {
      return { error: sessionExpiredError(url, operation) };
    }
    const retryResponse = await makeGraphQLRequest(url, document, variables);
    const { json: retryJson, snippet: retrySnippet } = await readResponseBody(retryResponse);
    if (retryResponse.status === 401 || (retryJson?.errors && hasAuthErrorMessage(retryJson.errors))) {
      return { error: wsapiAuthRejected(url, operation, retrySnippet) };
    }
    if (!retryJson) {
      return {
        error: buildRequestError({
          kind: 'http',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: retryResponse.status,
          bodySnippet: retrySnippet,
          detail: 'non-JSON response',
        }),
      };
    }
    if (retryJson.errors?.length) {
      return {
        error: buildRequestError({
          kind: 'graphql',
          endpoint: ENDPOINT_LABEL,
          url,
          operation,
          status: retryResponse.status,
          graphqlErrors: retryJson.errors,
        }),
      };
    }
    return { data: retryJson.data };
  }

  if (json.errors?.length) {
    return {
      error: buildRequestError({
        kind: 'graphql',
        endpoint: ENDPOINT_LABEL,
        url,
        operation,
        status: response.status,
        graphqlErrors: json.errors,
      }),
    };
  }

  return { data: json.data };
};

export const apiEndpoints = createApi({
  reducerPath: 'api',
  baseQuery: graphqlBaseQuery,
  tagTypes: [
    'Site',
    'User',
    'SftpConfig',
    'SftpLogs',
    'SftpSystemSettings',
    'SftpTenantConfig',
    'Settings',
    'SyncIterations',
    'TimeEntries',
    'AdminTenant',
    'AdminTenantFeatures',
    'FeatureFlag',
    'BackgroundServiceConfig',
    'WorkerConfig',
    'RestockCalcWork',
    'TrialRequest',
    'PaymentProcessor',
    'ParentCompany',
    'FirmwareRelease',
    'WhatsNew',
    'WhatsNewEmail',
    'CorrectionRun',
  ],
  endpoints: () => ({}), // Endpoints will be injected by code generation
});

export const api = apiEndpoints;

export const {
  // Generated hooks will be available here after code generation
} = apiEndpoints;
